import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, pick, useGame, type GameProps } from '../kit/shell';
import { GO, WAIT_DEFAULT } from '../phrases';
import { pausedMsSince } from '../../talk/talkOpen';

type Prompt = { prompt: string };

export default function WaitForGo(props: GameProps) {
  const game = useGame(props);
  const prompts = ((game.config?.prompts ?? []) as Prompt[]).filter((p) => p?.prompt);
  const minWait = game.num('minWait', 2);
  const maxWait = Math.max(minWait, game.num('maxWait', 6));
  const rounds = game.num('rounds', 5);
  const [phase, setPhase] = useState<'wait' | 'go' | 'early' | 'done' | 'finished'>('wait');
  const [message, setMessage] = useState(WAIT_DEFAULT);
  const [trial, setTrial] = useState<string | null>(null);
  const [turn, setTurn] = useState(0);
  const timer = useRef<number | null>(null);
  const busy = useRef(false);

  const start = useCallback(async (n: number) => {
    if (timer.current) window.clearInterval(timer.current);
    if (n >= rounds) { setPhase('finished'); return; }
    const msg = prompts.length ? pick(prompts).prompt : WAIT_DEFAULT;
    const id = await game.register({ message: msg, turn: n + 1 }, { value: 'GO' });
    if (!id) return;
    setTurn(n);
    setTrial(id);
    setMessage(msg);
    setPhase('wait');
    game.say(msg);
    const delay = (minWait + Math.random() * (maxWait - minWait)) * 1000;
    const startedAt = performance.now();
    timer.current = window.setInterval(() => {
      if (performance.now() - startedAt - pausedMsSince(startedAt) < delay) return;
      if (timer.current) window.clearInterval(timer.current);
      timer.current = null;
      setPhase('go');
      game.say(GO);
    }, 100);
  }, [rounds, minWait, maxWait, prompts.map((p) => p.prompt).join('|')]);

  useEffect(() => { if (game.config) void start(0); return () => { if (timer.current) window.clearInterval(timer.current); }; }, [game.config, start]);

  async function tap() {
    if (!trial || busy.current || (phase !== 'wait' && phase !== 'go')) return;
    busy.current = true;
    const early = phase === 'wait';
    if (timer.current) window.clearInterval(timer.current);
    const v = await game.respond(trial, early ? 'EARLY' : 'GO');
    busy.current = false;
    if (!v) return;
    setPhase(early ? 'early' : 'done');
    window.setTimeout(() => { void start(early ? turn : turn + 1); }, early ? 1400 : 1000);
  }

  const label = phase === 'go' ? 'Go!' : phase === 'done' ? 'Well waited!' : phase === 'early' ? 'Wait for green — again' : phase === 'finished' ? 'All done' : message;

  return (
    <GameFrame game={game} empty={false} emptyText="" onResume={() => { void start(turn); }} breakTrial={trial}>
      <div className="g-board g-wait">
        <p className="g-wait-count">Turn {Math.min(turn + 1, rounds)} of {rounds}</p>
        {phase === 'finished' ? (
          <div className="g-wait-end">
            <p className="g-prompt-text">All done</p>
            <button className="btn" onClick={() => void start(0)}>Play again</button>
          </div>
        ) : (
          <button type="button" className={`g-wait-btn is-${phase}`} onClick={() => void tap()} aria-live="polite">
            <span className="g-wait-sign" aria-hidden="true">{phase === 'go' || phase === 'done' ? '🟢' : '✋'}</span>
            <span className="g-wait-label">{label}</span>
          </button>
        )}
      </div>
    </GameFrame>
  );
}
